import type { RoundEvent } from "@slot/client-engine";
import type { ReelSpinMechanic } from "./ReelSpinMechanic";
import type { GridCodec, GridModel, Log, PresentationBarrier } from "@slot/presentation";
import type { BaseGridPresenter } from "@slot/mechanics-core";

/**
 * The reel mechanic's adapter to the client-engine event frames: a frame is decoded on
 * `prepareEventFrame` (after the spin barrier) and spun to on `presentEventFrame`; `paintGrid`
 * renders a grid without motion.
 */
export class ReelGridPresenter implements BaseGridPresenter {
  private readonly reel: ReelSpinMechanic;
  private readonly gridCodec: GridCodec;
  private readonly presentationBarrier: PresentationBarrier;
  private readonly log: Log;
  private preparedKey: string | null = null;
  private preparedModel: GridModel | null = null;

  constructor(input: { reel: ReelSpinMechanic; gridCodec: GridCodec; presentationBarrier: PresentationBarrier; log?: Log }) {
    this.reel = input.reel;
    this.gridCodec = input.gridCodec;
    this.presentationBarrier = input.presentationBarrier;
    this.log = input.log ?? (() => {});
  }

  public async paintGrid(encodedGridSnapshot: string): Promise<void> {
    this.reel.renderModel(this.gridCodec.decode(encodedGridSnapshot));
    this.preparedKey = null;
    this.preparedModel = null;
  }

  public async prepareEventFrame(event: RoundEvent): Promise<void> {
    const encoded = event.eventContent?.encodedGridSnapshot;
    if (!encoded) {
      this.log("ReelGridPresenter.prepareEventFrame: no encodedGridSnapshot, skipping");
      return;
    }
    await this.presentationBarrier.wait("spin");
    this.preparedModel = this.gridCodec.decode(encoded);
    this.preparedKey = this.computeEventKey(event);
  }

  public async presentEventFrame(event: RoundEvent): Promise<void> {
    const encoded = event.eventContent?.encodedGridSnapshot;
    if (!encoded) return;
    const key = this.computeEventKey(event);
    if (this.preparedKey !== key || !this.preparedModel) {
      await this.prepareEventFrame(event);
    }
    const model = this.preparedModel;
    this.preparedKey = null;
    this.preparedModel = null;
    if (!model) {
      this.log("ReelGridPresenter.presentEventFrame: no prepared model, skipping");
      return;
    }
    const anticipatedColumns = this.parseAnticipatedColumns(event);
    await this.reel.spinToModel(model, anticipatedColumns);
  }

  private parseAnticipatedColumns(event: RoundEvent): number[] | undefined {
    const raw = event.eventContent?.anticipatedColumns;
    if (!Array.isArray(raw) || raw.length === 0) return undefined;
    const valid = Array.from(new Set(raw.filter((c) => Number.isInteger(c) && c >= 0)));
    return valid.length > 0 ? valid : undefined;
  }

  /** Plain reels never remove symbols; the tumble layer overrides this. */
  public async removeSymbolsByMask(_positionMask: string): Promise<void> {}

  /** The round first: an event's number is unique only within its round. */
  private computeEventKey(event: RoundEvent): string {
    return `${event.roundIdentifier}:${event.eventTypeName}:${event.eventSequenceNumber ?? ""}:${event.eventContent?.encodedGridSnapshot ?? ""}`;
  }
}
